import { put, all, takeLatest } from 'redux-saga/effects';
import _data from './utils/_data';
import './app-actions';
import subscriptions from './sagas/subscriptions';
import plans from './sagas/plans';
// Util function to post to an api, parse results and dispatch loaded and error functions

// Error handler for a CRUD redux action
export function* errorHandler(action, prefix, preventSuccess, e) {
    const error = API.ajaxHandler(Actions[`${prefix}_ERROR`], e);
    yield put(error);
    action.onError && action.onError(error.error);
    if (preventSuccess) {
        throw e;
    }
}

// Success handler for a CRUD redux action
/**
 * 
 * @param {*} action includes onError callback, data, redux action type and more.
 * @param {*} prefix 
 * @param {*} apiResult 
 * @param {*} preventSuccess 
 * @param {*} dto 
 * @param {*} ignoreId false if id adds no value to the redux store for the type of action
 * @returns 
 */
export function* handleResponse(action, prefix, apiResult, preventSuccess, dto, ignoreId) {
    const data = dto ? dto(apiResult) : apiResult;
    const params = { type: Actions[`${prefix}_LOADED`], data };
    if (data.token) {
        // API.setStoredToken(data.token);
        _data.setToken(data.token);
    }
    if (action.id && !ignoreId) {
        params.index = action.id;
    }
    yield put(params);
    action.onSuccess && !preventSuccess && action.onSuccess(data);
    return data;
}

// GET request with standard response and error handler
export function* getAction(action, url, prefix, preventSuccess, dto, ignoreId) {
    try {
        const data = yield _data.get(url);
        return yield handleResponse(action, prefix, data, preventSuccess, dto, ignoreId);
    } catch (e) {
        if (e.url.includes('check-subscription')) {
            // If user has no subscriptions we throw 402
            // But we still want to handle it not as an error
            try {
                const payload = JSON.parse(e._bodyText);
                if (payload.subscriptions) {
                    return yield handleResponse(action, prefix, JSON.parse(e._bodyText), preventSuccess, dto, ignoreId);
                }
            // eslint-disable-next-line no-empty
            } catch (e) {}
        }
        yield errorHandler(action, prefix, preventSuccess, e);
    }
}

// PUT request with standard response and error handler
export function* updateAction(action, url, prefix, preventSuccess, dto, requestDto, append = true) {
    try {
        const data = yield _data.put(url, requestDto ? requestDto(action.data) : action.data);
        return yield handleResponse(action, prefix, data, preventSuccess, dto, append);
    } catch (e) {
        yield errorHandler(action, prefix, preventSuccess, e);
    }
}

// POST request with standard response and error handler
/**
 * 
 * @param {*} action contains action type, onSuccess/onError callbacks
 * @param {*} url 
 * @param {*} prefix action type
 * @param {*} preventSuccess if false does not call onSuccess callback
 * @param {*} dto handleResponse uses this callback to return response object
 * @param {*} requestDto 
 * @param {*} append 
 * @returns 
 */
export function* postAction(action, url, prefix, preventSuccess, dto, requestDto, append = true) {
    try {
        const data = yield _data.post(url, requestDto ? requestDto(action.data) : action.data);
        return yield handleResponse(action, prefix, data, preventSuccess, dto, append);
    } catch (e) {
        yield errorHandler(action, prefix, preventSuccess, e);
    }
}

export function* deleteAction(action, url, prefix, preventSuccess) {
    try {
        const data = yield _data.delete(url, {});
        return yield handleResponse(action, prefix, data, preventSuccess);
    } catch (e) {
        yield errorHandler(action, prefix, preventSuccess, e);
    }
}

// Called when the application starts up
export function* startup(action = {}) {
    try {
        const {
            ...rest
        } = action.data || {};
        const token = _.get(action, 'data.user.token');
        if (token) {
            _data.setToken(token);
        }

        const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
        yield put({ type: Actions.STARTUP_LOADED, data: { ready: true, isOnline, ...rest } });

        if (token && action.data.user) {
            // console.log('startup userData', userData);
            yield put({ type: Actions.LOGIN_LOADED, data: action.data.user });
        }
        if (action.onSuccess) {
            action.onSuccess();
        }
    } catch (e) {
        yield put(API.ajaxHandler(Actions.STARTUP_ERROR, e));
        action.onError && action.onError();
    }
}

export function* login(action) {
    const user = yield postAction(action, `${Project.api}/rest-auth/login/`, 'LOGIN', false, res => ({ ...res.user, token: res.key }));
    if (user) {
        yield AsyncStorage.setItem('user', JSON.stringify(user));
        _data.setToken(user.token);
    }
}

export function* loginWithToken(action) {
    try {
        const data = yield _data.get(`${Project.api}/rest-auth/user/`, null, { 'AUTHORIZATION': `Token ${action.token}` });
        const user = yield handleResponse(action, 'LOGIN_WITH_TOKEN', data, false, res => ({ ...res, token: action.token }));
        if (user) {
            yield AsyncStorage.setItem('user', JSON.stringify(user));
            _data.setToken(action.token);
        }
    } catch (e) {
        yield errorHandler(action, 'LOGIN_WITH_TOKEN', false, e);
    }
}

export function* register(action) {
    const user = yield postAction(action, `${Project.api}/rest-auth/registration/`, 'REGISTER', false, res => ({ ...res.user, token: res.key }));
    if (user) {
        yield AsyncStorage.setItem('user', JSON.stringify(user));
        _data.setToken(user.token);
    }
}

export function* logout(action) {
    yield put({ type: Actions.CLEAR_USER });
    // yield API.setStoredToken(null);
    // yield API.setCookie('user', null);
    // yield API.setStoredRefreshToken(null);
    yield AsyncStorage.clear();
    _data.setToken(null);
    API.logout();
    API.reset();
    action.onSuccess && action.onSuccess();
}

export function* confirmEmail(action) {
    yield postAction(action, `${Project.api}user/verify/email`, 'CONFIRM_EMAIL');
}

export function* updateUser(action) {
    yield updateAction(action, `${Project.api}user/${action.data.id}`, 'UPDATE_USER');
}

export function* sendStarClubTshirtOrderDetails(action) {
    yield postAction(action, `${Project.api}/users/star-club/order-tshirt`, 'SEND_STAR_CLUB_TSHIRT_ORDER_DETAILS');
}

// END OF YIELDS

function* rootSaga() {
    yield all([
        takeLatest(Actions.STARTUP, startup),
        takeLatest(Actions.LOGIN, login),
        takeLatest(Actions.REGISTER, register),
        takeLatest(Actions.LOGOUT, logout),
        takeLatest(Actions.CONFIRM_EMAIL, confirmEmail),
        takeLatest(Actions.UPDATE_USER, updateUser),
        takeLatest(Actions.LOGIN_WITH_TOKEN, loginWithToken),
        takeLatest(Actions.SEND_STAR_CLUB_TSHIRT_ORDER_DETAILS, sendStarClubTshirtOrderDetails),
        ...subscriptions,
        ...plans,
        // END OF TAKE_LATEST
    ]);
}

export default rootSaga;
